//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { BazaarvoiceDestinationClient } from '@apis/campaign-requests/sampling/prepaid-campaigns/clientReviewSyndicatedEdgesApiModel';
import { CampaignRequest } from '@apis/campaign-requests/shared/CampaignRequests';
import {
  useBazaarvoiceReviewContentDestinationsQuery,
  useGetClientRetailersQuery,
} from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { CampaignRequestRetailerField } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignRequestRetailerField';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { transformRetailersToDropdownOptions } from '@campaigns/utils/sampling/clientRetailerUtils';
import Button from '@components/atoms/button/Button';
import { PrimaryLinkButtonStyles } from '@components/atoms/button/buttonStyles';
import LoadingShimmer from '@components/atoms/loading-shimmer/LoadingShimmer';
import {
  BodyBoldText,
  BodyText,
  CaptionText,
} from '@components/atoms/text/Text';
import { RadioGroup } from '@components/primitives/radio-group/RadioGroup';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { getClientName } from '@redux/selectors/getSessionInfo';
import { copyTextToClipboard } from '@utils/copyToClipboard';
import { isDefined } from '@utils/filterPredicates';

const ContentDestination = {
  MySite: 'my-site',
  ASpecificRetailer: 'a-specific-retailer',
} as const;

type ContentDestination =
  (typeof ContentDestination)[keyof typeof ContentDestination];

const MAX_VISIBLE_DESTINATIONS = 4;

const sortDestinationsByReviews = (
  destinations: BazaarvoiceDestinationClient[],
) =>
  [...destinations].sort(
    (destinationA, destinationB) =>
      (destinationB.totalSyndicatedReviews ?? 0) -
      (destinationA.totalSyndicatedReviews ?? 0),
  );

export const ContentDestinationsStep = () => {
  const dispatch = useDispatch();
  const { setValue, watch } = useFormContext<CampaignRequest>();
  const clientName = useSelector(getClientName);
  const targetClientId = watch('targetClientId');
  const hasSelectedRetailer =
    isDefined(targetClientId) && targetClientId !== '';

  const [selectedDestination, setSelectedDestination] =
    useState<ContentDestination>(
      hasSelectedRetailer
        ? ContentDestination.ASpecificRetailer
        : ContentDestination.MySite,
    );

  const {
    data: queryDestinations = [],
    isError: isDestinationsError,
    isPending: isDestinationsLoading,
  } = useBazaarvoiceReviewContentDestinationsQuery(sortDestinationsByReviews);

  const { data: retailers = [] } = useGetClientRetailersQuery(
    transformRetailersToDropdownOptions,
  );

  const handleDestinationChange = (value: ContentDestination) => {
    setSelectedDestination(value);
    if (value === ContentDestination.MySite) {
      setValue('targetClientId', '');
    }
  };

  const handleCopyDestinations = async () => {
    const allNames = queryDestinations
      .map((destination) => destination.name)
      .join(', ');
    const success = await copyTextToClipboard(allNames);

    if (success) {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Full list of destinations copied to clipboard',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    }
  };

  const hasNetworkDestinations = queryDestinations.length > 0;
  const visibleDestinations = queryDestinations.slice(
    0,
    MAX_VISIBLE_DESTINATIONS,
  );
  const hiddenCount = Math.max(
    0,
    queryDestinations.length - MAX_VISIBLE_DESTINATIONS,
  );

  const getMySiteLabelText = () => {
    if (isDestinationsLoading) {
      return null;
    }
    if (hasNetworkDestinations) {
      return 'Content will display on your site and automatically distribute to any connected destinations, depending on products selected';
    }
    return 'Content will be displayed exclusively on your site.';
  };

  const mySiteLabelText = getMySiteLabelText();

  return (
    <Container>
      <RadioSection>
        <BodyText>
          Choose where to display content collected from this campaign.
        </BodyText>
        <StyledRadioGroup
          onValueChange={handleDestinationChange}
          value={selectedDestination}
        >
          <RadioOptionWrapper>
            <StyledRadioGroupItem
              id={ContentDestination.MySite}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.CONTENT_DESTINATIONS_MY_SITE_RADIO
              }
              value={ContentDestination.MySite}
            >
              <RadioGroup.Indicator />
            </StyledRadioGroupItem>
            <RadioLabelWrapper htmlFor={ContentDestination.MySite}>
              <BodyBoldText>
                {isDestinationsLoading ? (
                  <LoadingShimmer height={theme.spacings[3]} width='250px' />
                ) : (
                  <>
                    My Site ({clientName})
                    {hasNetworkDestinations && ' and Network'}
                  </>
                )}
              </BodyBoldText>
              <CaptionText>
                {isDestinationsLoading ? (
                  <LoadingShimmer height={theme.spacings[3]} width='600px' />
                ) : (
                  mySiteLabelText
                )}
              </CaptionText>
              {isDestinationsLoading && (
                <CaptionText>
                  <LoadingShimmer height={theme.spacings[3]} width='400px' />
                </CaptionText>
              )}
              {!isDestinationsLoading && (
                <CaptionText>
                  {isDestinationsError ? (
                    'We encountered an error loading the Bazaarvoice Syndication Network destinations.'
                  ) : (
                    <>
                      {visibleDestinations.map((dest) => dest.name).join(', ')}
                      {hiddenCount > 0 && (
                        <>
                          {' and '}
                          <Button
                            buttonStyles={PrimaryLinkButtonStyles}
                            onClick={handleCopyDestinations}
                            title='Click to copy full list'
                            trackingId={
                              SAMPLING_CAMPAIGNS_TRACKING_ID.CONTENT_DESTINATIONS_COPY_OTHERS_BUTTON
                            }
                            type='button'
                          >
                            {hiddenCount} others
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </CaptionText>
              )}
            </RadioLabelWrapper>
          </RadioOptionWrapper>

          <RadioOptionWrapper>
            <StyledRadioGroupItem
              id={ContentDestination.ASpecificRetailer}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.CONTENT_DESTINATIONS_SPECIFIC_RETAILER_RADIO
              }
              value={ContentDestination.ASpecificRetailer}
            >
              <RadioGroup.Indicator />
            </StyledRadioGroupItem>
            <RadioLabelWrapper htmlFor={ContentDestination.ASpecificRetailer}>
              <BodyBoldText>
                A Specific Retailer{hasNetworkDestinations && ' Only'}
              </BodyBoldText>
              <CaptionText>
                {hasNetworkDestinations
                  ? 'Content will be sent only to one retailer and will not appear on your site.'
                  : 'Content will be sent directly to a retailer of your choice.'}
              </CaptionText>
            </RadioLabelWrapper>
          </RadioOptionWrapper>
        </StyledRadioGroup>
        {selectedDestination === ContentDestination.ASpecificRetailer && (
          <RetailerSection>
            <BodyText>Chosen Retailer</BodyText>
            <CampaignRequestRetailerField
              retailerOptions={retailers}
              shouldShowCheckbox={false}
            />
          </RetailerSection>
        )}
      </RadioSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[4]};
`;

const RadioSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
`;

const RadioOptionWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const StyledRadioGroupItem = styled(RadioGroup.Item)`
  margin-top: ${({ theme }) => theme.spacings[2]};
`;

const RadioLabelWrapper = styled(RadioGroup.Label)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
  cursor: pointer;
`;

const RetailerSection = styled.div`
  width: 240px;
  margin-top: -${({ theme }) => theme.spacings[2]};
  margin-left: ${({ theme }) => theme.spacings[4]};
`;
